import * as React from 'react';
import OkCancelModal from '../../form-components/OkCancelModal.component';
import {SelectedWorkspaceObject, WorkspaceResource} from '../../../@types/workspace-types';
import SelectList from '../../form-components/SelectList.component';
import Typography from '@mui/material/Typography';
import {filterOnGroupCanEditResources, getResourceNameWithAscendants} from '../../../utils/resource-utils';
import {ResourceType} from '../../../@types/resource-type';
import SafetyModel from '../../../@types/safety-model';
import {useWorkspaceContext} from '../../../context/workspaceContext';

export default function MoveResourceModal(props: {
    open: boolean;
    title: string;
    object: SelectedWorkspaceObject | null;
    data: WorkspaceResource;
    onClose: () => void;
    onConfirm: (id: number | null) => void;
    loadData?: any;
}): React.ReactElement {

    const { open, title, object, data, onConfirm, onClose, loadData } = props;
    const { getAdditionalResource } = useWorkspaceContext();
    const [selectedResource, setSelectedResource] = React.useState<number>(0);
    const [selectedGroupIndex, setSelectedGroupIndex] = React.useState<number>(0);
    const [dataItems, setDataItems] = React.useState<any>();
    const [dataReady, setDataReady] = React.useState<boolean>(false);

    React.useEffect(() => {
        const items = resources2SelectListItems(data);
        setDataItems(items);
        if (items?.length > 0) {
            setDataReady(true);
            if (isEventSequence(object?.objectType) && selectedGroupIndex >= 0 && items[selectedGroupIndex]?.children?.length === 1) {
                setSelectedResource(items[selectedGroupIndex].children[0].id);
            }
        } else if (loadData) {
            if (!dataReady)
                loadData(setDataReady);
        }
    }, [data]);

    const onSelectResourceChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
        setSelectedResource(event.target.value as number);
    };

    const onSelectGroupChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
        setSelectedGroupIndex(dataItems.findIndex((item: { id: number; }) => item.id === event.target.value as number));
        setSelectedResource(0);
    };

    const isEventSequence = (resourceType: ResourceType | undefined): boolean => {
        return resourceType === ResourceType.EVENT_SEQUENCES || resourceType === ResourceType.EVENT_SEQUENCE_TREES || resourceType === ResourceType.EVENT_SEQUENCE_HISTORY;
    };

    const moveReady = (): boolean => {
        if (object?.objectType === ResourceType.GROUPS) {
            return true; // top-level (0) is a valid target
        }
        if (isEventSequence(object?.objectType)) {
            return selectedGroupIndex >= 0
                && dataItems?.length > 0
                && dataItems[selectedGroupIndex]?.children?.length > 0
                && selectedResource > 0;
        }
        return dataItems?.length > 0 && selectedResource > 0;
    };

    const groupBy = (safetyModels: SafetyModel[]): {} => {
        return safetyModels.reduce((groupWithChildren: any, sm: SafetyModel) => {
            const groupId = sm.group.id;
            if (groupId !== undefined) {
                if (!groupWithChildren[groupId]) {
                    groupWithChildren[groupId] = [];
                }
                groupWithChildren[groupId].push(sm);
            }
            return groupWithChildren;
        }, {});
    };

    function resources2SelectListItems(data: WorkspaceResource): { id: number; name: string; children?: { id: number; name: string; }[]; }[] {
        const items: { id: number; name: string; children?: { id: number; name: string; }[]; }[] = [];
        if (!data.resources) return items;

        if (data.objectType === ResourceType.GROUPS) {
            const sourceId = object?.object?.id;
            const resources = data.resources.filter((resource: any) => resource.id !== sourceId);
            for (const resource of resources) {
                items.push({
                    id: resource.id,
                    name: getResourceNameWithAscendants(resource, data.objectType),
                    children: [],
                });
            }
        } else if (data.objectType === ResourceType.SAFETY_MODELS || isEventSequence(data.objectType)) {
            const grouped = groupBy(data.resources);
            for (const [key, value] of Object.entries(grouped)) {
                const resource = value as SafetyModel[];
                items.push({
                    id: Number(key),
                    name: getResourceNameWithAscendants(resource[0].group, ResourceType.GROUPS),
                    children: resource.map(sm => ({ id: sm.id, name: sm.name })),
                });
            }
            // Add groups with no safety models
            const groups = filterOnGroupCanEditResources(getAdditionalResource(ResourceType.GROUPS)).resources;
            groups?.forEach((group: any) => {
                // @ts-ignore
                if (!grouped[group.id]) {
                    items.push({
                        id: group.id,
                        name: getResourceNameWithAscendants(group, ResourceType.GROUPS),
                    });
                }
            });
        }
        return items;
    }

    const resolvedTarget = (): number | null => {
        if (object?.objectType === ResourceType.GROUPS) {
            return selectedResource === 0 ? null : selectedResource;
        }
        return selectedResource;
    };

    return (
        <OkCancelModal
            open={open}
            title={title}
            okButtonText='Move'
            onClose={onClose}
            onConfirm={(): void => { onConfirm(resolvedTarget()); }}
            okToConfirm={moveReady}
        >
            <Typography variant='body1'>
                Select a destination
            </Typography>

            <hr />

            {(dataItems?.length > 0 || object?.objectType === ResourceType.GROUPS) && dataReady &&
                <SelectList
                    value={isEventSequence(object?.objectType) && selectedGroupIndex >= 0 ? dataItems[selectedGroupIndex]?.id : selectedResource}
                    label={object?.objectType === ResourceType.GROUPS ? 'Target Parent Working Area' : 'Target Working Area'}
                    items={dataItems}
                    showTopLevel={true}
                    topLevelLabel={object?.objectType === ResourceType.GROUPS ? '--Top Level (no parent)--' : '--Please Select--'}
                    onChange={isEventSequence(object?.objectType) ? onSelectGroupChange : onSelectResourceChange}
                />
            }

            <hr />

            {isEventSequence(object?.objectType) && dataItems?.length > 0 && selectedGroupIndex >= 0 && dataItems[selectedGroupIndex]?.children?.length > 0 && dataReady &&
                <SelectList
                    value={selectedResource}
                    label='Target Safety Model'
                    items={dataItems[selectedGroupIndex].children}
                    showTopLevel={true}
                    onChange={onSelectResourceChange}
                />
            }
        </OkCancelModal>
    );
}
